import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bar, Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './../../styles/admin/reportes_views.css';
import * as globals from './../../components/GlobalScripts.js';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, ChartDataLabels);

const Admin_reportes_notifications = () => {
    const path_api = localStorage.getItem('path_api');
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState({});
    const [metrics, setMetrics] = useState(null);

    // Filtro de fechas: se usa fecha actual y se resta un mes
    const options = { timeZone: 'America/Mexico_City', year: 'numeric', month: '2-digit', day: '2-digit' };
    const formattedToday = new Date().toLocaleDateString('en-CA', options);
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const formattedOneMonthAgo = oneMonthAgo.toLocaleDateString('en-CA', options);
    const [searchDateStart, setSearchDateStart] = useState(formattedOneMonthAgo);
    const [searchDateEnd, setSearchDateEnd] = useState(formattedToday);

    // Función para contenedores de gráficos horizontales (estado, ciudad y profesión)
    const horizontalChartContainerStyle = (count) => ({
        position: 'relative',
        height: count * 40 + 50 + 'px'
    });

    // Función para generar colores dinámicos en HSL
    const generateColors = (labels = []) => {
        return labels.map((_, index) => {
            const hue = Math.floor((360 / labels.length) * index);
            return `hsl(${hue}, 70%, 50%)`;
        });
    };

    useEffect(() => {
        const user_cache = localStorage.getItem('user');
        if (user_cache) {
            try {
                setUser(JSON.parse(user_cache));
            } catch (error) {
                console.error("Error al parsear el usuario", error);
            }
        }
        get_metrics(searchDateStart, searchDateEnd);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    const get_metrics = (dateStart, dateEnd) => {
        globals.openViewCharge();
        axios.post(`${path_api}/admin/reportes/notifications.php`, {
            rute: globals.getRuteBackend(),
            session_id: globals.get_session_id(),
            keylogin: globals.get_keylogin(),
            searchDateStart: dateStart,
            searchDateEnd: dateEnd,
        })
            .then(response => {
                if (response.data.mensaje === 'success') {
                    setMetrics(response.data.metrics);
                } else {
                    globals.createAlert({
                        type: "error",
                        msg_title: "Error",
                        msg: response.data.mensaje || "No se obtuvieron las métricas",
                        effect: "opacidad 2s, desplazamiento 2s",
                        time_life: 4000
                    });
                }
            })
            .catch(error => {
                globals.createAlert({
                    type: "error",
                    msg_title: "Error",
                    msg: "Error al cargar las métricas",
                    effect: "opacidad 2s, desplazamiento 2s",
                    time_life: 4000
                });
            })
            .finally(() => {
                globals.closeViewCharge();
            });
    };

    const handleSearchStartChange = (e) => {
        const value = e.target.value;
        setSearchDateStart(value);
        get_metrics(value, searchDateEnd);
    };

    const handleSearchEndChange = (e) => {
        const value = e.target.value;
        setSearchDateEnd(value);
        get_metrics(searchDateStart, value);
    };

    // --- Gráfica de pastel: Notificaciones por rol (Clientes vs Profesionales) ---
    const notificationsRole = metrics && metrics.notifications_by_role ? metrics.notifications_by_role : {};
    const totalNotificationsRole = Object.values(notificationsRole).reduce((sum, count) => sum + count, 0);
    const roleLabels = totalNotificationsRole > 0
        ? Object.keys(notificationsRole).map(role => 
            role === "1" ? `Clientes (${notificationsRole[role]})` : 
            role === "2" ? `Profesionales (${notificationsRole[role]})` :
            role === "4" ? `Administrador (${notificationsRole[role]})` : `Rol ${role} (${notificationsRole[role]})`
          )
        : [];
    const roleCounts = totalNotificationsRole > 0 ? Object.values(notificationsRole) : [];
    const notificationsRoleChartData = {
        labels: roleLabels,
        datasets: [
            {
                data: roleCounts,
                backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56'],
                hoverBackgroundColor: ['#36A2EB', '#FF6384', '#FFCE56']
            }
        ]
    };
    const notificationsRoleChartOptions = {
        maintainAspectRatio: false,
        aspectRatio: 1,
        plugins: {
            legend: { position: 'bottom' },
            datalabels: {
                display: true,
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: 4,
                padding: 4,
                font: { weight: 'bold', size: 14 },
                formatter: (value) => value
            }
        }
    };

    // --- Gráfica de pastel: Notificaciones por tipo de mensaje ---
    const notificationsMessage = metrics && metrics.notifications_by_message ? metrics.notifications_by_message : {};
    const totalNotificationsMessage = Object.values(notificationsMessage).reduce((sum, count) => sum + count, 0);
    const messageLabels = totalNotificationsMessage > 0
        ? Object.keys(notificationsMessage).map(key => `${key} (${notificationsMessage[key]})`)
        : [];
    const messageCounts = totalNotificationsMessage > 0 ? Object.values(notificationsMessage) : [];
    const notificationsMessageChartData = {
        labels: messageLabels,
        datasets: [
            {
                data: messageCounts,
                backgroundColor: ['#36A2EB', '#FF6384'],
                hoverBackgroundColor: ['#36A2EB', '#FF6384']
            }
        ]
    };
    const notificationsMessageChartOptions = {
        maintainAspectRatio: false,
        aspectRatio: 1,
        plugins: {
            legend: { position: 'bottom' },
            datalabels: {
                display: true,
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: 4,
                padding: 4,
                font: { weight: 'bold', size: 14 },
                formatter: (value) => value
            }
        }
    };

    // --- Gráficas horizontales: Distribución por estado, ciudad y profesión para cuentas profesionales ---
    const notificationsStateLabels = metrics && metrics.notifications_by_state ? Object.keys(metrics.notifications_by_state) : [];
    const notificationsStateColors = generateColors(notificationsStateLabels);
    const notificationsStateChartData = {
        labels: notificationsStateLabels,
        datasets: [
            {
                label: 'Notificaciones por estado',
                data: metrics && metrics.notifications_by_state ? Object.values(metrics.notifications_by_state) : [],
                backgroundColor: notificationsStateColors,
                hoverBackgroundColor: notificationsStateColors
            }
        ]
    };
    const notificationsStateChartOptions = {
        indexAxis: 'y',
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            datalabels: {
                display: true,
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: 4,
                padding: 4,
                font: { weight: 'bold', size: 14 },
                formatter: (value) => value
            }
        }
    };

    const notificationsCityLabels = metrics && metrics.notifications_by_city ? Object.keys(metrics.notifications_by_city) : [];
    const notificationsCityColors = generateColors(notificationsCityLabels);
    const notificationsCityChartData = {
        labels: notificationsCityLabels,
        datasets: [
            {
                label: 'Notificaciones por ciudad',
                data: metrics && metrics.notifications_by_city ? Object.values(metrics.notifications_by_city) : [],
                backgroundColor: notificationsCityColors,
                hoverBackgroundColor: notificationsCityColors
            }
        ]
    };
    const notificationsCityChartOptions = {
        indexAxis: 'y',
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            datalabels: {
                display: true,
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: 4,
                padding: 4,
                font: { weight: 'bold', size: 14 },
                formatter: (value) => value
            }
        }
    };

    const notificationsProfessionLabels = metrics && metrics.notifications_by_profession ? Object.keys(metrics.notifications_by_profession) : [];
    const notificationsProfessionColors = generateColors(notificationsProfessionLabels);
    const notificationsProfessionChartData = {
        labels: notificationsProfessionLabels,
        datasets: [
            {
                label: 'Notificaciones por profesión',
                data: metrics && metrics.notifications_by_profession ? Object.values(metrics.notifications_by_profession) : [],
                backgroundColor: notificationsProfessionColors,
                hoverBackgroundColor: notificationsProfessionColors
            }
        ]
    };
    const notificationsProfessionChartOptions = {
        indexAxis: 'y',
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            datalabels: {
                display: true,
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: 4,
                padding: 4,
                font: { weight: 'bold', size: 14 },
                formatter: (value) => value
            }
        }
    };

    return (
        <div className="page-reportes-views container-section flex-column gap-20">
            <button onClick={() => navigate(-1)} className="flex-row gap-5 button-back">
                <svg width="20" viewBox="0 0 100 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24.163 41.125L38.7085 55.6658L38.7088 55.6662C39.2779 56.2353 39.7294 56.9109 40.0374 57.6545C40.3454 58.3981 40.5039 59.1951 40.5039 60C40.5039 60.8049 40.3454 61.6018 40.0374 62.3454C39.7294 63.089 39.2779 63.7647 38.7088 64.3338C38.1397 64.9029 37.464 65.3544 36.7204 65.6624C35.9768 65.9704 35.1798 66.1289 34.375 66.1289C33.5701 66.1289 32.7732 65.9704 32.0296 65.6624C31.2859 65.3544 30.6103 64.9029 30.0412 64.3338L5.04386 39.3365C5.04297 39.3356 5.04207 39.3347 5.04118 39.3338M24.163 41.125L36.5875 12.2125C36.878 11.9219 37.1085 11.577 37.2658 11.1974C37.423 10.8178 37.5039 10.4109 37.5039 10C37.5039 9.5891 37.423 9.18222 37.2658 8.8026C37.1085 8.42298 36.878 8.07805 36.5875 7.7875C36.2969 7.49695 35.952 7.26647 35.5724 7.10923C35.1928 6.95198 34.7859 6.87105 34.375 6.87105C33.9641 6.87105 33.5572 6.95198 33.1776 7.10923C32.798 7.26647 32.453 7.49695 32.1625 7.7875L7.1625 32.7875C6.87148 33.0778 6.64059 33.4226 6.48305 33.8023C6.32551 34.1819 6.24441 34.589 6.24441 35C6.24441 35.411 6.32551 35.818 6.48305 36.1977C6.64059 36.5774 6.87148 36.9222 7.1625 37.2125L5.04118 39.3338" fill="var(--color-primary)" stroke="var(--color-primary)" strokeWidth="6" />
                </svg>
                <h4 style={{ color: "var(--color-primary)" }}>Volver</h4>
            </button>
            <h3>Reporte de notificaciones</h3>

            <div className="container-search">
                <div className="search">
                    <label>A partir de la fecha:</label>
                    <input type="date" value={searchDateStart} onChange={handleSearchStartChange} className="input-primary" />
                </div>
                <div className="search">
                    <label>Hasta la fecha:</label>
                    <input type="date" value={searchDateEnd} onChange={handleSearchEndChange} className="input-primary" />
                </div>
            </div>

            {metrics && (
                <div className="chart-grid-container">
                    {/* Gráfica de pastel: Notificaciones por rol */}
                    <div className="chart-item">
                        <h4>Enviadas a</h4>
                        <div style={{ position: 'relative', height: '300px' }}>
                            <Pie data={notificationsRoleChartData} options={notificationsRoleChartOptions} />
                        </div>
                    </div>

                    {/* Gráfica de pastel: Notificaciones por tipo de mensaje */}
                    <div className="chart-item">
                        <h4>Tipos</h4>
                        <div style={{ position: 'relative', height: '300px' }}>
                            <Pie data={notificationsMessageChartData} options={notificationsMessageChartOptions} />
                        </div>
                    </div>

                    {/* Distribución de notificaciones por estado*/}
                    {metrics.notifications_by_state && Object.keys(metrics.notifications_by_state).length > 0 && (
                        <div className="chart-item">
                            <h4>Notificaciones por estado</h4>
                            <div style={horizontalChartContainerStyle(Object.keys(metrics.notifications_by_state).length)}>
                                <Bar data={notificationsStateChartData} options={notificationsStateChartOptions} />
                            </div>
                        </div>
                    )}

                    {/* Distribución de notificaciones por ciudad */}
                    {metrics.notifications_by_city && Object.keys(metrics.notifications_by_city).length > 0 && (
                        <div className="chart-item">
                            <h4>Notificaciones por ciudad</h4>
                            <div style={horizontalChartContainerStyle(Object.keys(metrics.notifications_by_city).length)}>
                                <Bar data={notificationsCityChartData} options={notificationsCityChartOptions} />
                            </div>
                        </div>
                    )}

                    {/* Distribución de notificaciones por profesión */}
                    {metrics.notifications_by_profession && Object.keys(metrics.notifications_by_profession).length > 0 && (
                        <div className="chart-item">
                            <h4>Notificaciones por profesión principal</h4>
                            <div style={horizontalChartContainerStyle(Object.keys(metrics.notifications_by_profession).length)}>
                                <Bar data={notificationsProfessionChartData} options={notificationsProfessionChartOptions} />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Admin_reportes_notifications;
